import { Block, Symbology } from '../document'
import { mmToDots, wrap } from '../text'

/**
 * Lays a label's blocks out top to bottom in printer dots, so TSPL / ZPL / EPL only translate positions into their own
 * commands. Character cells follow the printers' built-in fonts at 203 dpi (12 × 20 dots), scaled for 300 dpi.
 */
export type Placed =
  | { kind: 'text'; x: number; y: number; text: string; scale: 1 | 2; bold: boolean }
  | { kind: 'barcode'; x: number; y: number; value: string; symbology: Symbology; height: number; narrow: number; showText: boolean }
  | { kind: 'qr'; x: number; y: number; value: string; cell: number }
  | { kind: 'line'; x: number; y: number; width: number }

export interface LabelLayout {
  width: number
  height: number
  items: Placed[]
}

const barcodeModules = (value: string, symbology: Symbology) => (symbology === 'ean13' ? 95 : 11 * (value.length + 3) + 13)

export const layoutLabel = (blocks: Block[], p: Record<string, any>): LabelLayout => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const dpi = Number(p.dpi)
  const k = dpi / 203
  const width = mmToDots(Number(p.label_width_mm), dpi)
  const height = mmToDots(Number(p.label_height_mm), dpi)
  const margin = mmToDots(Number(p.margin_mm ?? 2), dpi)
  const charW = Math.round(12 * k)
  const charH = Math.round(20 * k)
  const gap = Math.round(4 * k)
  const avail = width - 2 * margin
  const items: Placed[] = []
  let y = margin

  const xFor = (w: number, align = 'left') =>
    align === 'center' ? margin + Math.max(0, Math.round((avail - w) / 2)) : align === 'right' ? margin + Math.max(0, avail - w) : margin

  for (const b of blocks) {
    switch (b.type) {
      case 'text': {
        const scale = b.size === 2 ? 2 : 1
        for (const text of wrap(b.text, Math.max(1, Math.floor(avail / (charW * scale))))) {
          items.push({ kind: 'text', x: xFor(text.length * charW * scale, b.align), y, text, scale, bold: !!b.bold })
          y += charH * scale + gap
        }
        break
      }
      case 'row': {
        const cols = Math.max(1, Math.floor(avail / charW))
        const room = Math.max(0, cols - b.right.length - 1)
        items.push({ kind: 'text', x: margin, y, text: b.left.slice(0, room), scale: 1, bold: !!b.bold })
        items.push({ kind: 'text', x: xFor(b.right.length * charW, 'right'), y, text: b.right, scale: 1, bold: !!b.bold })
        y += charH + gap
        break
      }
      case 'rule':
        items.push({ kind: 'line', x: margin, y: y + gap, width: avail })
        y += gap * 3
        break
      case 'feed':
        y += b.lines * (charH + gap)
        break
      case 'barcode': {
        const narrow = Math.max(1, Math.min(3, Math.floor(avail / barcodeModules(b.value, b.symbology))))
        const h = mmToDots(b.height ?? 8, dpi)
        items.push({
          kind: 'barcode',
          x: xFor(barcodeModules(b.value, b.symbology) * narrow, b.align ?? 'center'),
          y,
          value: b.value,
          symbology: b.symbology,
          height: h,
          narrow,
          showText: b.show_text !== false,
        })
        y += h + (b.show_text !== false ? charH : 0) + gap
        break
      }
      case 'qr': {
        const size = mmToDots(b.size ?? 15, dpi)
        const cell = Math.max(2, Math.floor(size / 29))
        items.push({ kind: 'qr', x: xFor(cell * 29, b.align ?? 'center'), y, value: b.value, cell })
        y += cell * 29 + gap
        break
      }
      case 'image':
        break
    }
  }
  return { width, height, items }
}
